import React, { useState } from 'react';
import { Bug } from 'lucide-react';
import clsx from 'clsx';
import { useErrorPresence } from '../../hooks/useErrorPresence';
import { errorRingBuffer } from '../../utils/errorRingBuffer';
import BugReportModal from './BugReportModal';

interface BugReportButtonProps {
  /**
   * 'mobile' keeps the button above the /m/* bottom nav at every width. The
   * default placement drops to the page corner from `sm` up, which on an
   * iPad (where the bottom nav is still shown) sits on top of the first tab.
   */
  placement?: 'default' | 'mobile';
}

export default function BugReportButton({ placement = 'default' }: BugReportButtonProps) {
  const [open, setOpen] = useState(false);
  const { hasRecentError, errorCount } = useErrorPresence();

  const recentErrors = errorRingBuffer.snapshot();
  const latestMsg = recentErrors.at(-1)?.msg?.slice(0, 100) ?? null;

  return (
    <>
      <div
        className={clsx(
          'fixed z-50 group',
          placement === 'mobile' ? 'left-4' : 'bottom-24 left-4 sm:bottom-6 sm:left-6'
        )}
        style={placement === 'mobile' ? { bottom: 'calc(var(--bottom-nav-total) + 1rem)' } : undefined}
      >
        {hasRecentError && latestMsg && (
          <div
            className={clsx(
              'absolute bottom-full left-0 mb-2',
              'hidden group-hover:block',
              'max-w-xs w-max',
              'bg-surface border border-line text-ink text-xs shadow-lg rounded-lg px-2 py-1.5',
              'pointer-events-none'
            )}
          >
            <span className="font-medium text-red-600 dark:text-red-400">Recent error: </span>
            <span className="font-mono break-all">{latestMsg}</span>
          </div>
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Report a bug"
          className={clsx(
            'flex items-center justify-center',
            'h-12 w-12 rounded-full shadow-lg',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-offset-2',
            hasRecentError
              ? [
                  'bg-red-600 hover:bg-red-700 text-white',
                  'focus:ring-red-500',
                  'animate-pulse',
                ]
              : [
                  'bg-surface border border-line text-ink-muted hover:text-ink hover:bg-canvas',
                  'focus:ring-blue-500',
                ]
          )}
        >
          <Bug className="h-5 w-5" />
          {hasRecentError && errorCount > 0 && (
            <span
              className={clsx(
                'absolute -top-1 -right-1',
                'flex h-5 w-5 items-center justify-center',
                'rounded-full bg-red-500 text-white',
                'text-xs font-bold leading-none',
                'ring-2 ring-white dark:ring-gray-900'
              )}
            >
              {errorCount > 9 ? '9+' : errorCount}
            </span>
          )}
        </button>
      </div>

      <BugReportModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
